import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { Formik, FormikHelpers } from 'formik'
import { Box, Button, Grid, Stack, TextField } from '@mui/material'
import { RootState } from '../redux'
import { createSector, updateSector, hideSector } from '../redux/slices/sectors'
import { getProjects } from '../redux/slices/projects'
import useSector from '../hooks/useSector'
import useAuth from '../hooks/useAuth'
import SectorInput from '../types/SectorInput'
import ProjectSelect from './ProjectSelect'
import sectorService from '../services/sectorService'
import { createErrorOrSuccessNotification } from '../redux/slices/notifications'
import { deleteError, setError } from '../redux/slices/errors'
import { SectorErrors } from '../enums/SectorErrors'
import { NotificationType } from '../enums/NotificationType'
import { createUserNotification } from '../utils/createUserNotification'
import { Sector } from '../types'
import { LoadingButton } from '@mui/lab'

export interface SectorFormProps {
  value?: SectorInput
  hidden?: Array<keyof SectorInput>
  disabled?: Array<keyof SectorInput>
  onCancel?: () => void
  onSubmit?: (values: SectorInput, helpers: FormikHelpers<SectorInput>) => Promise<void>
  beforeSubmit?: (values: SectorInput, helpers: FormikHelpers<SectorInput>) => void
  afterSubmit?: (sector: Sector) => void
}

export default function SectorForm({
  value,
  hidden = ['project' as keyof SectorInput],
  disabled = [],
  onCancel,
  onSubmit,
  beforeSubmit,
  afterSubmit
}: SectorFormProps) {
  const dispatch = useDispatch()

  const [t] = useTranslation('common')

  const sector = useSector()

  const isLoadingProjects: boolean = useSelector((state: RootState) => state.projects.loading)

  const initialValues: SectorInput = {
    name: sector?.name ?? '',
    number: sector?.number ?? 0,
    project: sector?.project?.id ?? null,
    ...value
  }

  const validationSchema = Yup.object().shape({
    project: Yup.number().required(
      t('isRequired', {
        field: t('Sector.project')
      })
    ),
    // FIXME: Add unique condition:
    // - Number should be unique among sectors of the selected project
    name: Yup.string().required(
      t('isRequired', {
        field: t('Sector.name')
      })
    ),
    number: Yup.number()
  })

  useEffect(() => {
    dispatch(getProjects())
  }, [])

  function isHidden(field: string): boolean {
    return hidden.includes(field as keyof SectorInput)
  }

  function isDisabled(field: string): boolean {
    return disabled.includes(field as keyof SectorInput)
  }

  const { currentUser } = useAuth()

  async function handleSubmit(
    values: SectorInput,
    { resetForm, setStatus, setSubmitting, ...helpers }: FormikHelpers<SectorInput>
  ): Promise<void> {
    try {
      if (onSubmit) {
        return onSubmit(values, {
          resetForm,
          setStatus,
          setSubmitting,
          ...helpers
        })
      } else {
        if (beforeSubmit) {
          beforeSubmit(values, {
            resetForm,
            setStatus,
            setSubmitting,
            ...helpers
          })
        }

        let createdOrUpdatedSector: Sector
        if (sector === undefined) {
          const createdSector = await sectorService.createSector(values)

          dispatch(createSector(createdSector))
          dispatch(
            createErrorOrSuccessNotification(NotificationType.SUCCESS, t('sectorCreateSuccess'))
          )

          createdOrUpdatedSector = createdSector
        } else {
          const updatedSector = await sectorService.updateSector(sector.id, values)

          dispatch(updateSector(updatedSector))
          dispatch(
            createErrorOrSuccessNotification(NotificationType.SUCCESS, t('sectorUpdateSuccess'))
          )

          createdOrUpdatedSector = updatedSector
        }

        resetForm()
        setStatus({ sent: true })
        dispatch(hideSector())

        if (afterSubmit) {
          afterSubmit(createdOrUpdatedSector)
        }
      }
    } catch (error) {
      setStatus({ sent: false })
      // FIXME: Set validation errors.
      dispatch(
        setError({
          type: sector ? SectorErrors.EDIT : SectorErrors.CREATE,
          error: error
        })
      )

      //Create message
      const errorMessage = createUserNotification({
        user: currentUser,
        type: sector ? SectorErrors.EDIT : SectorErrors.CREATE,
        error: error
      })

      //Dispatch error message
      dispatch<unknown>(
        createErrorOrSuccessNotification(
          NotificationType.WARNING,
          t(errorMessage.key) + t(errorMessage.message)
        )
      )
      //TODO: Do not delete errors until submitting form has passed if error is validation error from the backend
      dispatch(deleteError(sector ? SectorErrors.EDIT : SectorErrors.CREATE))
    } finally {
      setSubmitting(false)
    }
  }

  function handleCancel(): void {
    if (onCancel) {
      onCancel()
    } else {
      dispatch(hideSector())
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched, isSubmitting, handleBlur, handleChange, handleSubmit }) => (
        <Box mt={2}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={6}>
              {!isHidden('project') && (
                <Grid item xs={12}>
                  <ProjectSelect
                    name="project"
                    label={t('Sector.project')}
                    value={values.project}
                    error={Boolean(touched.project && errors.project)}
                    helperText={touched.project && errors.project}
                    disabled={isLoadingProjects || isSubmitting || isDisabled('project')}
                    variant="outlined"
                    fullWidth
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  name="name"
                  label={t('Sector.name')}
                  value={values.name}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                  disabled={isSubmitting || isDisabled('name')}
                  fullWidth
                  variant="outlined"
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
                  <Button type="button" disabled={isSubmitting} onClick={handleCancel}>
                    {t('cancel')}
                  </Button>

                  <LoadingButton
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isLoadingProjects}
                    loading={isSubmitting}
                  >
                    {t('save')}
                  </LoadingButton>
                </Stack>
              </Grid>
            </Grid>
          </form>
        </Box>
      )}
    </Formik>
  )
}
